/* Shared cart presentation for the Quiz and product pages. No checkout or marketing requests. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.PXCartUI = factory();
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  let nextId = 0;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

  function create(options = {}) {
    const commerce = options.commerce;
    if (!commerce || typeof commerce.getCart !== 'function') throw new TypeError('A cart adapter is required.');
    const native = (options.mode || commerce.mode) === 'shopify';
    const currency = options.currency || commerce.currency || 'USD';
    const cartUrl = options.cartUrl || commerce.cartUrl || '/cart';
    const nativeHeaderSelector = 'a#cart-icon-bubble, a.a6-cart-link';
    const dialog = document.createElement('dialog');
    const titleId = `px-cart-title-${++nextId}`;
    dialog.className = 'px-cart-dialog';
    dialog.setAttribute('aria-labelledby', titleId);
    document.body.append(dialog);
    const cartStrings = JSON.parse(document.getElementById('a6-cart-live-strings')?.textContent || '{}');
    let trigger = null;
    let destroyed = false;
    let removing = false;
    let showRequest = 0;
    let countRequest = 0;
    let lastCart = null;
    let returningFromCart = false;

    const money = (value, unit = currency) => new Intl.NumberFormat('en-US', { style: 'currency', currency: unit }).format((Number(value) || 0) / 100);
    const head = () => `<header class="px-cart-header"><h2 id="${titleId}">Your cart</h2><button type="button" class="px-cart-close" data-px-cart-close aria-label="Close cart">×</button></header>`;
    const benefitTemplate = id => document.getElementById(id)?.innerHTML || '';
    const unavailable = handle => globalThis.PXBackorder?.isUnavailable?.(handle) === true;
    const viewCart = (hasUnavailable = false) => native ? `<a class="px-cart-checkout" href="${escape(cartUrl)}">${hasUnavailable?'Review cart':'View cart &amp; checkout'} <span aria-hidden="true">↗</span></a>` : '';

    function paintCount(cart) {
      if (destroyed || !cart) return;
      document.querySelectorAll(native ? nativeHeaderSelector : '.px-nav-cart').forEach(link => {
        link.setAttribute('aria-label', `Cart, ${cart.itemCount} ${cart.itemCount === 1 ? 'item' : 'items'}`);
        link.dataset.pxCartCount = String(cart.itemCount);
        delete link.dataset.quizCount;
        if (native) {
          let bubble = link.querySelector('.cart-count-bubble');
          if (!cart.itemCount) {
            bubble?.remove();
          } else {
            if (!bubble) {
              bubble = document.createElement('div');
              bubble.className = 'cart-count-bubble';
              link.append(bubble);
            }
            const count = document.createElement('span');
            count.setAttribute('aria-hidden', 'true');
            count.textContent = cart.itemCount < 100 ? String(cart.itemCount) : '99+';
            bubble.replaceChildren(count);
          }
        }
      });
    }

    async function updateCount(cart, review = false) {
      const request = ++countRequest;
      try {
        const next = cart || await commerce.getCart(review ? { review: true } : undefined);
        if (!destroyed && request === countRequest) paintCount(next);
        return next;
      } catch (_) { return null; }
    }

    function render(cart) {
      lastCart = cart;
      const hasUnavailable = cart.items.some(item=>unavailable(item.handle));
      const lines = cart.items.map(item => {
        const unit = item.isQuizProduct === false ? (item.quantity === 1 ? 'item' : 'items') : (item.quantity === 1 ? 'pair' : 'pairs');
        const detail = [...[item.color, item.size].filter(Boolean), `${item.quantity} ${unit}`].join(' · ');
        return `<article class="px-cart-item" data-line-key="${escape(item.key || item.id)}" data-product-handle="${escape(item.handle)}" data-a6-line-quantity="${item.quantity}"${unavailable(item.handle)?' data-a6-unavailable="true"':''}>${item.image ? `<img class="px-cart-photo" src="${escape(item.image)}" alt="${escape(item.title)}" width="160" height="160">` : '<div class="px-cart-no-photo" aria-hidden="true"></div>'}<div class="px-cart-item-info"><h3>${escape(item.title)}</h3><p>${escape(detail)}</p>${globalThis.PXBackorder?.note(item.handle,true)||''}<strong>${escape(money(item.lineTotal ?? item.price * item.quantity, cart.currency))}</strong>${native ? `<div class="a6-cart-stepper"><button type="button" data-a6-cart-step="-1" aria-label="${escape(cartStrings.decrease)} ${escape(item.title)}" ${item.quantity <= 1 ? 'disabled' : ''}>−</button><input type="number" data-a6-cart-quantity-input value="${item.quantity}" min="1" step="1"${unavailable(item.handle)?` max="${item.quantity}"`:''} inputmode="numeric" aria-label="${escape(cartStrings.quantity)} ${escape(item.title)}"><button type="button" data-a6-cart-step="1" aria-label="${escape(cartStrings.increase)} ${escape(item.title)}"${unavailable(item.handle)?' disabled':''}>+</button></div>` : ''}<button type="button" class="px-cart-remove" data-px-cart-remove="${escape(item.key || item.id)}" aria-label="Remove ${escape(item.title)}${item.size ? `, size ${escape(item.size)}` : ''}">Remove</button></div></article>`;
      }).join('');
      const discounts = Array.isArray(cart.cartDiscounts) ? cart.cartDiscounts : [];
      const discountSummary = discounts.length ? `${Number.isSafeInteger(cart.itemsSubtotal) ? `<div class="px-cart-total"><span>Items subtotal</span><span>${escape(money(cart.itemsSubtotal, cart.currency))}</span></div>` : ''}${discounts.map(discount => `<div class="px-cart-total px-cart-discount"><span>${escape(discount.title)}</span><span>−${escape(money(discount.amount, cart.currency))}</span></div>`).join('')}` : '';
      dialog.innerHTML = `${head()}<div class="px-cart-body">${cart.items.length ? `<div class="px-cart-items">${lines}</div>${native ? '<div data-a6-drawer-offer></div>' + benefitTemplate('a6-pair-savings-template') : ''}` : '<div class="px-cart-empty"><h3>A little room for comfort.</h3><p>Your cart is empty. Choose a cushioning feel and your size to get started.</p></div>'}${discountSummary}<p class="px-cart-message" data-a6-cart-status role="status" aria-live="polite"></p></div><footer class="px-cart-footer"><div class="px-cart-total"><span>${discounts.length ? 'Total' : 'Subtotal'}</span><strong>${escape(money(cart.total, cart.currency))}</strong></div>${discounts.length ? `<p class="a6-cart-saved">${escape(cartStrings.saved)} ${escape(money(discounts.reduce((sum,d)=>sum+d.amount,0),cart.currency))}</p>` : ''}${native ? `${hasUnavailable?`<p class="a6-backorder-cart-note" role="status">${escape(cartStrings.unavailable)}</p>`:globalThis.PXBackorder?.cartSummary(cart.items)?`<p class="a6-backorder-cart-note">${escape(globalThis.PXBackorder.cartSummary(cart.items))}</p>`:''}${benefitTemplate('a6-shipping-note-template')}${benefitTemplate('a6-cart-charge-note-template')}${viewCart(hasUnavailable)}` : '<p class="px-cart-note">Design preview · your selections stay in this browser session. Checkout is not connected.</p>'}<button type="button" class="px-cart-continue" data-px-cart-close>Continue exploring <span aria-hidden="true">→</span></button></footer>`;
      dialog.removeAttribute('aria-busy');
      updateCount(cart);
      if (native) window.A6CartLive?.loadOffer(dialog);
    }

    async function show(cart) {
      if (destroyed) return null;
      const request = ++showRequest;
      if (!dialog.open) {
        trigger = document.activeElement;
        dialog.innerHTML = `${head()}<div class="px-cart-body"><p class="px-cart-loading" role="status">Loading your cart…</p></div>`;
        dialog.showModal();
        document.documentElement.classList.add('px-cart-open');
      }
      dialog.setAttribute('aria-busy', 'true');
      try {
        const next = cart || await commerce.getCart({ review: true });
        if (destroyed || request !== showRequest) return null;
        render(next);
        if (dialog.open) dialog.querySelector('[data-px-cart-close]')?.focus({ preventScroll: true });
        return next;
      } catch (_) {
        if (destroyed || request !== showRequest) return null;
        dialog.removeAttribute('aria-busy');
        dialog.innerHTML = `${head()}<div class="px-cart-body"><p class="px-cart-message" role="status">We could not load your cart. Please try again.</p><button type="button" class="px-cart-retry" data-px-cart-retry>Reload cart</button></div><footer class="px-cart-footer">${viewCart()}<button type="button" class="px-cart-continue" data-px-cart-close>Continue exploring <span aria-hidden="true">→</span></button></footer>`;
        if (dialog.open) dialog.querySelector('[data-px-cart-close]')?.focus({ preventScroll: true });
        return null;
      }
    }

    async function handleDialogClick(event) {
      if (event.target.closest('[data-px-cart-close]')) { dialog.close(); return; }
      if (event.target.closest('[data-px-cart-retry]')) { if (!removing) show(); return; }
      if (event.target === dialog) {
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
        return;
      }
      const button = event.target.closest('[data-px-cart-remove]');
      if (!button || removing) return;
      removing = true;
      dialog.setAttribute('aria-busy', 'true');
      dialog.querySelectorAll('[data-px-cart-remove]').forEach(item => { item.disabled = true; });
      button.textContent = 'Removing…';
      const lineIndex = lastCart?.items.findIndex(item => String(item.key || item.id) === button.dataset.pxCartRemove) ?? 0;
      try {
        const clickedLine = lastCart?.items.find(item => String(item.key || item.id) === button.dataset.pxCartRemove);
        const cart = await commerce.remove(button.dataset.pxCartRemove, clickedLine?.id);
        if (!destroyed) {
          render(cart);
          if (dialog.open) {
            const buttons = dialog.querySelectorAll('[data-px-cart-remove]');
            (buttons[Math.min(Math.max(lineIndex, 0), buttons.length - 1)] || dialog.querySelector('[data-px-cart-close]'))?.focus({ preventScroll: true });
          }
        }
      } catch (error) {
        if (!destroyed) {
          if (error.code === 'STALE_LINE' && error.cart) {
            render(error.cart);
            if (dialog.open) dialog.querySelector('[data-px-cart-close]')?.focus({ preventScroll: true });
          }
          dialog.removeAttribute('aria-busy');
          dialog.querySelectorAll('[data-px-cart-remove]').forEach(item => { item.disabled = Boolean(error.uncertain); });
          button.textContent = 'Remove';
          const message = dialog.querySelector('.px-cart-message');
          if (message) message.textContent = error.uncertain ? 'We could not confirm the change. Open your cart to check it before trying again.' : (error.message || 'This item could not be removed. Please try again.');
        }
      } finally { removing = false; }
    }

    function restoreFocus() {
      document.documentElement.classList.remove('px-cart-open');
      if (destroyed) return;
      const fallback = document.querySelector('[data-px-cart-trigger]') || document.querySelector('.quiz-add') || document.querySelector('.px-nav-burger') || document.querySelector('.px-nav-cart');
      const target = trigger?.isConnected && trigger !== document.body && trigger.getClientRects().length ? trigger : fallback;
      target?.focus({ preventScroll: true });
      if (typeof options.onClose === 'function') options.onClose();
    }

    function handlePageClick(event) {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest(native ? nativeHeaderSelector : 'a[href="./index.html?read=cart"], .px-nav-cart, [data-read="cart"]');
      if (!link) {
        const destination = event.target.closest('a[href]');
        if (native && destination && (!destination.target || destination.target === '_self') && !destination.hasAttribute?.('download')) {
          try {
            const target = new URL(destination.href, window.location.href);
            const fullCart = new URL(cartUrl, window.location.href);
            if (target.origin === window.location.origin && target.pathname === fullCart.pathname) returningFromCart = true;
          } catch (_) {}
        }
        return;
      }
      event.preventDefault();
      // Legacy homepage cart buttons also match the general reading-panel handler.
      if (native || link.matches('[data-read="cart"]')) event.stopPropagation();
      window.PXNavigation?.close();
      show();
    }

    function handleHeaderKey(event) {
      if (!native || event.defaultPrevented || ![' ', 'Spacebar'].includes(event.key) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!event.target.closest(nativeHeaderSelector)) return;
      event.preventDefault();
      event.stopPropagation();
      window.PXNavigation?.close();
      show();
    }

    function refreshOnReturn(event) {
      if (!event.persisted) return;
      if (dialog.open) show();
      else updateCount(null, returningFromCart);
      returningFromCart = false;
    }
    const refreshCart = event => { if (!destroyed && native) { render(commerce.normalizeCart(event.detail)); window.A6CartLive?.enhance(); } };
    document.addEventListener('a6:cart-updated', refreshCart);
    dialog.addEventListener('click', handleDialogClick);
    dialog.addEventListener('close', restoreFocus);
    document.addEventListener('click', handlePageClick, true);
    document.addEventListener('keydown', handleHeaderKey, true);
    window.addEventListener('pageshow', refreshOnReturn);
    updateCount();

    return Object.freeze({ show, updateCount, destroy() {
      destroyed = true;
      document.removeEventListener('a6:cart-updated', refreshCart);
      ++showRequest;
      ++countRequest;
      document.removeEventListener('click', handlePageClick, true);
      document.removeEventListener('keydown', handleHeaderKey, true);
      window.removeEventListener('pageshow', refreshOnReturn);
      dialog.removeEventListener('click', handleDialogClick);
      dialog.removeEventListener('close', restoreFocus);
      if (dialog.open) dialog.close();
      document.documentElement.classList.remove('px-cart-open');
      dialog.remove();
    } });
  }
  return Object.freeze({ create });
}));
